import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { t } from "../utils/i18n";
import { formatScore } from "../utils/format";
import ScoreBadge from "../components/ScoreBadge";
import CreateVenueModal from "../components/CreateVenueModal";
import WelcomePopup from "../components/WelcomePopup";
import PostCard from "../components/PostCard";
import StoriesBar from "../components/Stories";
import PhotoLightbox from "../components/PhotoLightbox";
import PageMeta from "../components/PageMeta";
import { usePaymentReturnBanner } from "../utils/paymentReturn";
import FastCard from "../components/FastCard";
import { LightningIcon } from "../components/FastCategoryIcons";
import { FAST_COLOR } from "../styles/tokens";
import { TuneIcon, ChevronDownIcon } from "../components/FilterIcon";
import { useIsMobile } from "../hooks/useIsMobile";

const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><polygon points="6,4 20,12 6,20" /></svg>
);
const EyeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
);

// Choix du TYPE de contenu — un seul actif à la fois, réduit à une puce "Tout ▾" qui ouvre la
// liste complète au clic (avant : 4 puces dont "Gratuits" posées côte à côte en permanence).
const FILTERS = ["all", "passions", "powers", "feedbacks"];
// "feedbacks" n'a pas de clé i18n à son propre nom (jamais utilisé comme puce avant ce jour) —
// les 3 autres si (voir grep price_inherit-anchored batch), donc un seul cas particulier ici
// plutôt qu'une clé i18n dupliquée pour "all"/"passions"/"powers".
const TYPE_LABEL_KEY: Record<string, string> = { feedbacks: "filter_feedbacks" };
// Filtres additionnels, combinables entre eux et avec FILTERS — regroupés derrière l'icône
// Filtres plutôt que d'ajouter encore des puces en permanence à l'écran. Chaque valeur pointe
// vers sa propre clé i18n (voir PRICE_LABEL_KEY/DATE_LABEL_KEY), "free" excepté qui réutilise la
// clé déjà existante (même mot que la puce "Gratuits" historique).
const PRICE_FILTERS = ["all", "free", "paid"];
const DATE_FILTERS = ["all", "today", "week", "month"];
const PRICE_LABEL_KEY: Record<string, string> = { all: "filter_all_prices", free: "free", paid: "filter_paid" };
const DATE_LABEL_KEY: Record<string, string> = { all: "filter_all_dates", today: "msg_today", week: "filter_this_week", month: "filter_this_month" };
const matchesDateFilter = (eventDate: string | null | undefined, key: string): boolean => {
  if (key === "all") return true;
  if (!eventDate) return false;
  const d = new Date(eventDate);
  const now = new Date();
  if (key === "today") return d.toDateString() === now.toDateString();
  const end = new Date(now);
  if (key === "week") end.setDate(end.getDate() + 7);
  else if (key === "month") end.setMonth(end.getMonth() + 1);
  else return true;
  return d >= now && d <= end;
};
// activeFilter est au pluriel (puces de la barre de filtres) ; le paramètre `type` de
// /algorithm/agenda attend le singulier stocké sur publications.type.
const FILTER_TYPE_PARAM: Record<string, string> = { passions: "passion", powers: "power", feedbacks: "feedback" };
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const agendaDayLabel = (dateStr: string) => {
  const d = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === tomorrow.toDateString()) return "Demain";
  return capitalize(d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }));
};
const NEARBY_PROFILES_RADIUS_KM = 50;

// L'endpoint /users/discover garde toujours les amis visibles quelle que soit la distance
// (façon Snapchat Map, utile pour Explorer/Search) — mais pour la suggestion de profils du
// fil Home, on veut strictement des gens "autour de toi", donc on refiltre côté client.
const distanceKm = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};
// Une ligne de profils suggérés réapparaît toutes les FEED_PROFILES_INTERVAL publications —
// assez fréquent pour animer le fil, assez espacé pour ne pas noyer les vraies publications.
const FEED_PROFILES_INTERVAL = 5;

// Réutilisée à la fois épinglée en haut du fil (mobile, faute de sidebar) et interclassée
// périodiquement dans le fil (mobile et desktop) — même carte que le module sidebar desktop,
// en légèrement plus compact pour s'intégrer au flux de publications.
function NearbyProfilesStrip({ users, navigate, title }: { users: any[]; navigate: (path: string) => void; title: string }) {
  if (users.length === 0) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0a0a0a", marginBottom: 10 }}>{title}</div>
      <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
        {users.map((u: any, i: number) => (
          <div key={u.id || i} onClick={() => navigate("/profile/" + u.id)} style={{ position: "relative", width: 130, flexShrink: 0, borderRadius: 16, overflow: "hidden", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
            <div style={{
              height: 170, display: "flex", alignItems: "flex-end", padding: "0 10px 10px",
              backgroundColor: "#0a0a0a",
              backgroundImage: u.avatar_url ? `linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.75) 100%), url(${u.avatar_url})` : "linear-gradient(160deg, #0a0a0aCC 0%, #0a0a0a 100%)",
              backgroundSize: "cover", backgroundPosition: "center",
            }}>
              <div style={{ color: "white", fontWeight: 700, fontSize: 13 }}>{u.name}</div>
            </div>
            {u.is_online && <div style={{ position: "absolute", top: 8, right: 8, width: 10, height: 10, borderRadius: "50%", backgroundColor: "#4CAF50", border: "2px solid white" }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// Bloc à part façon Facebook — pas mélangé aux publications, juste sous les Stories/filtres.
// Deux vignettes remplissent la largeur sur mobile, le reste se découvre au scroll horizontal.
function LiveStrip({ lives, onOpen }: { lives: any[]; onOpen: (i: number) => void }) {
  if (lives.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#0C32FF" }} />
        <span style={{ fontSize: 15, fontWeight: 800, color: "#0C32FF", letterSpacing: 0.5 }}>LIVE</span>
      </div>
      <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
        {lives.map((live: any, i: number) => (
          <div key={live.id} onClick={() => onOpen(i)} style={{ position: "relative", width: "46%", minWidth: 150, flexShrink: 0, aspectRatio: "9/16", borderRadius: 16, overflow: "hidden", cursor: "pointer", backgroundColor: "#0a0a0a" }}>
            <video src={live.url} muted preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.7) 100%)" }} />
            <span style={{ position: "absolute", top: 10, left: 10, fontSize: 10.5, fontWeight: 800, color: "white", backgroundColor: "#e74c3c", borderRadius: 6, padding: "2px 6px", letterSpacing: 0.5 }}>LIVE</span>
            {!!live.views_count && (
              <span style={{ position: "absolute", top: 10, right: 10, display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: "white" }}>
                <EyeIcon /> {live.views_count}
              </span>
            )}
            <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PlayIcon />
              </span>
            </span>
            <div style={{ position: "absolute", bottom: 10, left: 10, right: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "#333", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 9, fontWeight: 700 }}>
                {live.author_avatar ? <img src={live.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (live.author_name || "?")[0]}
              </div>
              <span style={{ color: "white", fontSize: 12, fontWeight: 700, textShadow: "0 1px 4px rgba(0,0,0,0.5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{live.author_name}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Carrousel FAST (ex-QUICK, renommé — la communauté n'a pas adhéré au nom d'origine) — même
// architecture que LiveStrip (fetch propre, section dédiée pinnée à une position fixe du fil)
// plutôt qu'un widget de suggestion latéral : FAST est un contenu éphémère et urgent, pas une
// simple recommandation. Placé juste après la 1ère publication du fil (décision du fondateur),
// pas avant (comme Stories) ni après 2-3 posts (comme les Lives). Le label reprend le "fast" en
// minuscule du bouton Explorer plutôt que "LIVE" en capitales — sa propre petite signature.
function FastStrip({ fasts }: { fasts: any[] }) {
  if (fasts.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <LightningIcon size={13} color={FAST_COLOR} />
        <span style={{ fontSize: 17, fontWeight: 800, fontStyle: "italic", color: "#0a0a0a", letterSpacing: -0.3 }}>Fast</span>
      </div>
      <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
        {fasts.map(q => <FastCard key={q.id} fast={q} />)}
      </div>
    </div>
  );
}

// Vue "Agenda" — les événements datés triés par jour (voir GET /algorithm/agenda, pas plafonné
// par score d'engagement contrairement au fil normal). Une ligne compacte plutôt que le plein
// PostCard (pensé pour un fil social, pas une liste dense triée par date).
function AgendaSection({ items, navigate, lang }: { items: any[]; navigate: (path: string) => void; lang: string }) {
  const groups = React.useMemo(() => {
    const byDay = new Map<string, any[]>();
    items.forEach(it => {
      const key = new Date(it.event_date).toDateString();
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(it);
    });
    return Array.from(byDay.entries());
  }, [items]);

  if (items.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="M3 10h18"/></svg>
        <div style={{ fontSize: 14 }}>{t(lang, "home_agenda_empty")}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      {groups.map(([day, dayItems]) => (
        <div key={day} style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0a0a0a", marginBottom: 8 }}>{agendaDayLabel(dayItems[0].event_date)}</div>
          {dayItems.map((it: any) => {
            const image = it.cover_url || it.photos?.[0]?.url;
            const time = new Date(it.event_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
            return (
              <div key={it.id} onClick={() => navigate(`/publication/${it.id}`)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: "1px solid #F0F0F0", cursor: "pointer" }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, backgroundColor: "#F5F5F5", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {image ? <img src={image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="M3 10h18"/></svg>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0a0a0a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</div>
                  <div style={{ fontSize: 12.5, color: "#999", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.display_name} · {time}</div>
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: it.is_free ? "#1D9E75" : "#0a0a0a", flexShrink: 0 }}>
                  {it.is_free ? t(lang, "price_free") : `${Number(it.price).toFixed(0)} €`}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const { lang, token, user, setUser } = useApp();
  const navigate = useNavigate();
  usePaymentReturnBanner();

  const isMobile = useIsMobile();

  const [activeFilter, setActiveFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [agendaMode, setAgendaMode] = useState(false);
  const [agendaItems, setAgendaItems] = useState<any[]>([]);
  const [loadingAgenda, setLoadingAgenda] = useState(false);

  const [posts, setPosts] = useState<any[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(true);

  const [nearbyUsers, setNearbyUsers] = useState<any[]>([]);
  const [nearbyVenues, setNearbyVenues] = useState<any[]>([]);
  const [lives, setLives] = useState<any[]>([]);
  const [liveIndex, setLiveIndex] = useState<number | null>(null);
  const [fasts, setFasts] = useState<any[]>([]);
  const [showCreateVenue, setShowCreateVenue] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (user && user.welcome_shown === false) setShowWelcome(true);
  }, [user]);

  useEffect(() => {
    if (!token) return;
    api.get("/users/lives/feed").then(data => setLives(data.lives || [])).catch(() => {});
    api.get("/fasts/feed").then(data => setFasts(data.fasts || [])).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const hasLocation = !!(user?.latitude && user?.longitude);
    const params = hasLocation ? `?lat=${user.latitude}&lng=${user.longitude}&radius=${NEARBY_PROFILES_RADIUS_KM}` : "";
    api.get(`/users/discover${params}`).then(data => {
      let users = data.users || [];
      if (hasLocation) {
        users = users.filter((u: any) => u.latitude && u.longitude && distanceKm(user.latitude, user.longitude, u.latitude, u.longitude) <= NEARBY_PROFILES_RADIUS_KM);
      }
      setNearbyUsers(users.slice(0, 40));
    }).catch(() => {});
    api.get(`/venues/nearby${params}`).then(data => setNearbyVenues((data.venues || []).slice(0, 8))).catch(() => {});
  }, [token, user?.latitude, user?.longitude]);

  useEffect(() => {
    if (token) {
      api.get("/algorithm/feed")
        .then(data => { if (data && Array.isArray(data.publications)) setPosts(data.publications); })
        .catch(() => {})
        .finally(() => setLoadingFeed(false));
    } else {
      setLoadingFeed(false);
    }
  }, [token]);

  // Un filtre de DATE actif bascule automatiquement sur la source Agenda, même sans avoir cliqué
  // le bouton "Agenda" — le fil normal est plafonné à 20 publications choisies par score
  // d'engagement, un champ event_date y est rare, donc filtrer ce fil déjà tronqué par date
  // renvoyait presque toujours une liste vide quel que soit le filtre choisi (le prix, lui, n'a
  // pas ce problème : is_free est présent sur toutes les publications, donc reste filtré sur le
  // fil normal). Le bouton "Agenda" reste un accès direct indépendant du filtre.
  const useAgendaSource = agendaMode || dateFilter !== "all";

  // Vue Agenda — une source de données à part (GET /algorithm/agenda), pas un simple filtrage
  // client de `posts` : voir le commentaire sur getAgendaPublications côté serveur. Se recharge
  // à chaque changement de filtre tant que cette source est active.
  useEffect(() => {
    if (!useAgendaSource) return;
    setLoadingAgenda(true);
    const params = new URLSearchParams();
    if (activeFilter !== "all") params.set("type", FILTER_TYPE_PARAM[activeFilter] || activeFilter);
    if (priceFilter !== "all") params.set("price", priceFilter);
    if (dateFilter !== "all") params.set("date", dateFilter);
    api.get(`/algorithm/agenda?${params.toString()}`)
      .then(data => { if (data && Array.isArray(data.publications)) setAgendaItems(data.publications); })
      .catch(() => {})
      .finally(() => setLoadingAgenda(false));
  }, [useAgendaSource, activeFilter, priceFilter, dateFilter]);

  const filtered = posts.filter((p: any) => {
    if (activeFilter === "passions" && p.type !== "passion") return false;
    if (activeFilter === "powers" && p.type !== "power") return false;
    if (activeFilter === "feedbacks" && p.type !== "feedback") return false;
    if (priceFilter === "free" && !p.is_free) return false;
    if (priceFilter === "paid" && p.is_free) return false;
    if (!matchesDateFilter(p.event_date, dateFilter)) return false;
    return true;
  });
  const hasExtraFilters = priceFilter !== "all" || dateFilter !== "all";

  // Fait tourner la fenêtre de 10 profils affichés à chaque insertion (au lieu de toujours
  // montrer les 10 mêmes en tête de liste), en bouclant sur le pool récupéré.
  const profilesForStrip = (stripIndex: number) => {
    if (nearbyUsers.length === 0) return [];
    const start = (stripIndex * 10) % nearbyUsers.length;
    return [...nearbyUsers.slice(start), ...nearbyUsers.slice(0, start)].slice(0, 10);
  };

  // Une seule requête de participation possible par publication : elle part toujours comme
  // une demande ("pending") que l'organisateur devra accepter ou refuser — jamais d'inscription
  // automatique, quel que soit le type d'activité.
  const participate = (post: any) => {
    if (post.my_participation_status) return;
    const success_url = `${window.location.origin}${window.location.pathname}?payment=success`;
    const cancel_url = `${window.location.origin}${window.location.pathname}?payment=cancelled`;
    api.post(`/publications/${post.id}/participate`, { success_url, cancel_url, lang }).then(data => {
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data?.status) {
        setPosts(prev => prev.map(p => p.id === post.id ? { ...p, my_participation_status: data.status } : p));
      } else if (data?.error) {
        alert(data.error);
      }
    }).catch(() => {});
  };

  // "Top" et "Envieux" sont deux réactions indépendantes sur un même feedback (on peut trouver
  // une expérience géniale ET en être envieux) — chacune se toggle sans effacer l'autre.
  const toggleReaction = (post: any, type: "interested" | "top" | "envieux") => {
    api.post(`/publications/${post.id}/react`, { type }).then(data => {
      if (data?.action) {
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          tops_count: data.tops_count ?? p.tops_count,
          envieux_count: data.envieux_count ?? p.envieux_count,
          interested_count: data.interested_count ?? p.interested_count,
          my_reactions: data.action === "added"
            ? [...(p.my_reactions || []), type]
            : (p.my_reactions || []).filter((r: string) => r !== type),
        } : p));
      }
    }).catch(() => {});
  };

  return (
    <>
    <PageMeta title={t(lang, "home")} />
    <div style={{ display: "flex", maxWidth: 1100, margin: "0 auto", width: "100%" }}>
      <style>{`.oltodo-no-scrollbar::-webkit-scrollbar{display:none} .oltodo-no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}`}</style>

      {/* COLONNE PRINCIPALE — FIL */}
      <div style={{ flex: 1, minWidth: 0, padding: isMobile ? "12px" : "20px 16px" }}>

        {/* Relance "ajoute ta photo" — comptes existants sans avatar (avatar_required=false,
            grandfathered lors du déploiement de l'obligation, jamais bloqués). Pas de bouton
            pour la masquer : elle disparaît d'elle-même une fois la photo ajoutée. */}
        {user && user.avatar_required === false && !user.avatar_url && (
          <button
            onClick={() => navigate("/profile")}
            style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "12px 16px", borderRadius: 14, border: "1.5px solid #0a0a0a", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", marginBottom: 16 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
            </svg>
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{t(lang, "home_add_photo_banner")}</span>
            <span style={{ fontSize: 18 }}>→</span>
          </button>
        )}

        {/* Stories — moments éphémères (24h) des événements/rituels/communautés en cours */}
        <StoriesBar />

        {/* Filtres — "Tout ▾" remplace les anciennes puces Passions/Powers posées côte à côte :
            le type de contenu se choisit maintenant dans un menu, et Prix/Date rejoignent
            l'icône Filtres à part — même logique combinable qu'avant, juste rangée pour ne pas
            empiler les puces. */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button onClick={() => setShowTypeMenu(true)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 20, cursor: "pointer",
            fontSize: 13, fontWeight: 600, border: "1.5px solid #E0E0E0", backgroundColor: "white", color: "#0a0a0a",
          }}>
            {t(lang, TYPE_LABEL_KEY[activeFilter] || activeFilter) || activeFilter}
            <ChevronDownIcon size={13} color="#666" />
          </button>
          <button onClick={() => setShowFilterSheet(true)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 20, cursor: "pointer",
            fontSize: 13, fontWeight: 600, position: "relative",
            border: hasExtraFilters ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0",
            backgroundColor: hasExtraFilters ? "#0a0a0a" : "white",
            color: hasExtraFilters ? "white" : "#666",
          }}>
            <TuneIcon size={16} color={hasExtraFilters ? "white" : "#666"} />
            {t(lang, "filters_label") || "Filtres"}
            {hasExtraFilters && <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: "#FF3B30" }} />}
          </button>
          {/* Bascule vers la vue Agenda (événements triés par date) — une source à part de la
              publication algorithmique, pas un simple tri de `filtered` (voir getAgendaPublications
              côté serveur : pas plafonné par score d'engagement, contrairement à /feed). */}
          <button onClick={() => setAgendaMode(v => !v)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 20, cursor: "pointer",
            fontSize: 13, fontWeight: 600,
            border: useAgendaSource ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0",
            backgroundColor: useAgendaSource ? "#0a0a0a" : "white",
            color: useAgendaSource ? "white" : "#666",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={useAgendaSource ? "white" : "#666"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="M3 10h18"/></svg>
            {t(lang, "agenda_title")}
          </button>
        </div>

        {/* Menu "Tout ▾" — sélection unique du type de contenu, ferme dès qu'on choisit. */}
        {showTypeMenu && (
          <div onClick={() => setShowTypeMenu(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            {/* maxHeight + overflowY : sans ça, sur un écran bas (ou le chrome d'un navigateur
                mobile qui rogne la hauteur visible), le contenu dépassait du bas de l'écran, ce
                qui rendait le bouton de validation inatteignable. */}
            <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto", borderRadius: "20px 20px 0 0", padding: "24px 22px", boxSizing: "border-box" }}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>{t(lang, "home_show_label") || "Afficher"}</div>
              {FILTERS.map(f => {
                const active = activeFilter === f;
                return (
                  <button
                    key={f} onClick={() => { setActiveFilter(f); setShowTypeMenu(false); }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "14px 0", background: "none", border: "none", borderTop: "1px solid #F0F0F0", cursor: "pointer", textAlign: "left" }}
                  >
                    <span style={{ fontSize: 15, fontWeight: active ? 700 : 500, color: active ? "#0a0a0a" : "#666" }}>{t(lang, TYPE_LABEL_KEY[f] || f) || f}</span>
                    {active && <CheckIcon />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filtres — Date et Prix, combinables, appliqués immédiatement au clic comme le reste
            des filtres de ce fil (pas de bouton "Appliquer" séparé). */}
        {showFilterSheet && (
          <div onClick={() => setShowFilterSheet(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            {/* maxHeight + overflowY : sans ça, sur un écran bas (ou le chrome d'un navigateur
                mobile qui rogne la hauteur visible), le contenu dépassait du bas de l'écran, ce
                qui rendait le bouton de validation inatteignable. */}
            <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto", borderRadius: "20px 20px 0 0", padding: "24px 22px", boxSizing: "border-box" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{t(lang, "filters_label") || "Filtres"}</div>
                {hasExtraFilters && (
                  <button onClick={() => { setPriceFilter("all"); setDateFilter("all"); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#666" }}>
                    {t(lang, "filters_reset") || "Réinitialiser"}
                  </button>
                )}
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#999" }}>{t(lang, "filters_date_section") || "Date"}</div>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginTop: 10 }}>
                {DATE_FILTERS.map(f => {
                  const active = dateFilter === f;
                  return (
                    <button key={f} onClick={() => setDateFilter(f)} style={{
                      padding: "9px 14px", borderRadius: 18, cursor: "pointer", fontSize: 13, fontWeight: 600,
                      border: active ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0",
                      backgroundColor: active ? "#0a0a0a" : "#F8F8F8", color: active ? "white" : "#666",
                    }}>{t(lang, DATE_LABEL_KEY[f]) || f}</button>
                  );
                })}
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#999", marginTop: 18 }}>{t(lang, "filters_price_section") || "Prix"}</div>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginTop: 10 }}>
                {PRICE_FILTERS.map(f => {
                  const active = priceFilter === f;
                  return (
                    <button key={f} onClick={() => setPriceFilter(f)} style={{
                      padding: "9px 14px", borderRadius: 18, cursor: "pointer", fontSize: 13, fontWeight: 600,
                      border: active ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0",
                      backgroundColor: active ? "#0a0a0a" : "#F8F8F8", color: active ? "white" : "#666",
                    }}>{t(lang, PRICE_LABEL_KEY[f]) || f}</button>
                  );
                })}
              </div>

              <button onClick={() => setShowFilterSheet(false)} style={{ width: "100%", marginTop: 24, padding: "15px", borderRadius: 14, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                {(t(lang, "filters_see_results") || "Voir {n} résultats").replace("{n}", String(filtered.length))}
              </button>
            </div>
          </div>
        )}

        {useAgendaSource ? (
          loadingAgenda ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#bbb", fontSize: 14 }}>{t(lang, "loading_ellipsis")}</div>
          ) : (
            <AgendaSection items={agendaItems} navigate={navigate} lang={lang} />
          )
        ) : (
          <>
            {loadingFeed && (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#bbb", fontSize: 14 }}>{t(lang, "loading_ellipsis")}</div>
            )}

            {!loadingFeed && filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#0a0a0a", marginBottom: 8 }}>{t(lang, "home_be_first")}</div>
                <div style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{t(lang, "home_no_posts")}<br/>{t(lang, "home_share_first")}</div>
                <button onClick={() => navigate("/create")} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>+ {t(lang, "create_publication_entry")}</button>
              </div>
            )}

            {filtered.map((post: any, i: number) => (
              <React.Fragment key={post.id}>
                <PostCard post={post} lang={lang} user={user} navigate={navigate} toggleReaction={toggleReaction} participate={participate} onHideAuthor={userId => setPosts(prev => prev.filter(p => p.user_id !== userId))} />
                {/* Juste après la 1ère publication — FAST est éphémère (max 1h), il ne doit jamais
                    être enterré plus bas dans le fil. */}
                {i === 0 && <FastStrip fasts={fasts} />}
                {/* Bloc à part façon Facebook, jamais en tête de fil — inséré une seule fois après
                    2-3 publications (ou après la dernière si le fil en compte moins). */}
                {i === Math.min(2, filtered.length - 1) && (
                  <LiveStrip lives={lives} onOpen={setLiveIndex} />
                )}
                {(i + 1) % FEED_PROFILES_INTERVAL === 0 && (
                  <NearbyProfilesStrip users={profilesForStrip(Math.floor(i / FEED_PROFILES_INTERVAL))} navigate={navigate} title={t(lang, "home_profiles_near")} />
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      {/* SIDEBAR DROITE */}
      <div style={{ display: isMobile ? "none" : "block", width: 300, flexShrink: 0, overflowY: "auto", padding: "20px 16px 20px 0" }}>

        {/* SCORE SOCIAL */}
        <div onClick={() => navigate("/rating")} style={{ backgroundColor: "white", borderRadius: 16, padding: 16, border: "1px solid #F0F0F0", marginBottom: 24, cursor: "pointer" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{t(lang, "score_social_title")}</div>
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><ScoreBadge score={user?.score} size={72} /></div>
            <div style={{ fontSize: 11, color: "#999" }}>{Number(user?.score || 0) >= 8 ? t(lang, "home_score_excellent") : Number(user?.score || 0) >= 6 ? t(lang, "home_score_good") : t(lang, "home_score_progress")}</div>
          </div>
        </div>

        {/* MEMBRES PRÈS DE TOI */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <span style={{ fontSize: 20, fontWeight: 800, color: "#0a0a0a" }}>{nearbyUsers.length}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#0a0a0a", marginLeft: 6 }}>{t(lang, "home_members_near")}</span>
            </div>
          </div>
          {nearbyUsers.length === 0 ? (
            <div style={{ fontSize: 13, color: "#bbb", padding: "12px 0" }}>{t(lang, "home_nobody_near")}</div>
          ) : (
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8 }}>
              {nearbyUsers.map((u: any, i: number) => (
                <div key={i} onClick={() => navigate("/profile/" + u.id)} style={{ position: "relative", width: 155, flexShrink: 0, borderRadius: 18, overflow: "hidden", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
                  <div style={{
                    height: 200, display: "flex", alignItems: "flex-end", padding: "0 12px 12px",
                    backgroundColor: "#0a0a0a",
                    backgroundImage: u.avatar_url ? `linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.75) 100%), url(${u.avatar_url})` : "linear-gradient(160deg, #0a0a0aCC 0%, #0a0a0a 100%)",
                    backgroundSize: "cover", backgroundPosition: "center",
                  }}>
                    <div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{u.name}</div>
                  </div>
                  {u.is_online && <div style={{ position: "absolute", top: 10, right: 10, width: 12, height: 12, borderRadius: "50%", backgroundColor: "#4CAF50", border: "2px solid white" }} />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* LIEUX À SUIVRE */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <span style={{ fontSize: 20, fontWeight: 800, color: "#0a0a0a" }}>{nearbyVenues.length}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#0a0a0a", marginLeft: 6 }}>{t(lang, "home_venues_to_follow")}</span>
            </div>
          </div>
          {nearbyVenues.length === 0 ? (
            <div style={{ padding: "12px 0" }}>
              <div style={{ fontSize: 13, color: "#bbb", marginBottom: 10 }}>{t(lang, "home_no_venues_near")}</div>
              <button onClick={() => setShowCreateVenue(true)} style={{ backgroundColor: "#F5F5F5", border: "1px solid #E0E0E0", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 600, color: "#0a0a0a", cursor: "pointer" }}>
                + {t(lang, "home_propose_venue")}
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8 }}>
              {nearbyVenues.map((v: any, i: number) => (
                <div key={i} onClick={() => navigate("/venue/" + v.id)} style={{ width: 155, flexShrink: 0, borderRadius: 18, overflow: "hidden", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
                  <div style={{
                    height: 200, display: "flex", alignItems: "flex-end", padding: "0 12px 12px",
                    backgroundColor: "#0a0a0a",
                    backgroundImage: v.cover_url ? `linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.75) 100%), url(${v.cover_url})` : "linear-gradient(160deg, #0a0a0aCC 0%, #0a0a0a 100%)",
                    backgroundSize: "cover", backgroundPosition: "center",
                  }}>
                    <div style={{ color: "white", fontWeight: 700, fontSize: 14 }}>{v.name}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {showCreateVenue && (
        <CreateVenueModal
          onClose={() => setShowCreateVenue(false)}
          onCreated={v => setNearbyVenues(prev => [v, ...prev])}
        />
      )}

      {showWelcome && user && (
        <WelcomePopup
          user={user}
          onDone={patch => {
            const next = { ...user, ...patch };
            setUser(next);
            setShowWelcome(false);
          }}
        />
      )}

      {liveIndex !== null && (
        <PhotoLightbox
          photos={lives}
          index={liveIndex}
          onIndexChange={setLiveIndex}
          onClose={() => setLiveIndex(null)}
          isOwner={false}
          ownerId=""
          ownerName=""
          currentUserId={user?.id}
          onLikeToggled={(photoId, liked, likesCount) => setLives(prev => prev.map(p => p.id === photoId ? { ...p, liked_by_me: liked, likes_count: likesCount } : p))}
          onLocationUpdated={() => {}}
          onViewed={photoId => setLives(prev => prev.map(p => p.id === photoId ? { ...p, views_count: (p.views_count || 0) + 1 } : p))}
          onDeleted={() => {}}
        />
      )}
    </div>
    </>
  );
}
